import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Save, Loader2, ChevronDown, ChevronUp, Eye, EyeOff } from 'lucide-react';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { contentAPI } from '../../services/api';

const RegulatoryServicesEditor = () => {
  const [services, setServices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    try { const res = await contentAPI.getAdmin('regulatory_services'); setServices(Array.isArray(res.data) ? res.data : []); }
    catch { toast.error('Failed to load'); }
  }, []);

  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setSaving(true);
    try { await contentAPI.update('regulatory_services', services); toast.success('Regulatory services saved!'); }
    catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const update = (idx, field, value) => {
    const copy = [...services];
    copy[idx] = { ...copy[idx], [field]: value };
    setServices(copy);
  };

  const updateArray = (idx, field, value) => {
    update(idx, field, value.split('\n').filter(s => s.trim()));
  };

  return (
    <div className="space-y-4 max-w-4xl">
      <p className="text-sm text-gray-500">Edit each regulatory service. Click to expand. Use the switch to show or hide a service on the public site.</p>
      {services.map((svc, idx) => (
        <Card key={svc.id || idx} className={svc.hidden ? 'opacity-60 border-dashed' : ''} data-testid={`reg-service-card-${svc.id}`}>
          <CardHeader className="cursor-pointer" onClick={() => setExpanded(expanded === idx ? null : idx)}>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <CardTitle className="text-lg truncate">{svc.title}</CardTitle>
                <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${svc.hidden ? 'bg-gray-100 text-gray-600' : 'bg-emerald-50 text-emerald-700'}`} data-testid={`reg-service-status-${svc.id}`}>
                  {svc.hidden ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  {svc.hidden ? 'Hidden' : 'Visible'}
                </span>
              </div>
              <div className="flex items-center gap-3" onClick={e => e.stopPropagation()}>
                <Switch checked={!svc.hidden} onCheckedChange={v => update(idx, 'hidden', !v)} data-testid={`reg-service-toggle-${svc.id}`} />
                {expanded === idx ? <ChevronUp className="w-5 h-5 cursor-pointer" onClick={() => setExpanded(null)} /> : <ChevronDown className="w-5 h-5 cursor-pointer" onClick={() => setExpanded(idx)} />}
              </div>
            </div>
          </CardHeader>
          {expanded === idx && (
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Title</label>
                  <Input value={svc.title || ''} onChange={e => update(idx, 'title', e.target.value)} />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Slug</label>
                  <Input value={svc.slug || ''} onChange={e => update(idx, 'slug', e.target.value)} />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Timeline</label>
                  <Input value={svc.timeline || ''} onChange={e => update(idx, 'timeline', e.target.value)} />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Image URL</label>
                  <Input value={svc.image || ''} onChange={e => update(idx, 'image', e.target.value)} />
                </div>
              </div>
              {svc.image && <img src={svc.image} alt="Preview" className="h-24 w-full object-cover rounded-lg" />}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Description</label>
                <Textarea rows={3} value={svc.description || ''} onChange={e => update(idx, 'description', e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Process Steps (one per line)</label>
                <Textarea rows={4} value={(svc.process || []).join('\n')} onChange={e => updateArray(idx, 'process', e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Required Documents (one per line)</label>
                <Textarea rows={4} value={(svc.documents || []).join('\n')} onChange={e => updateArray(idx, 'documents', e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Benefits</label>
                <Textarea rows={2} value={svc.benefits || ''} onChange={e => update(idx, 'benefits', e.target.value)} />
              </div>
            </CardContent>
          )}
        </Card>
      ))}
      <Button onClick={save} disabled={saving} className="w-full" data-testid="save-regulatory">
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save Regulatory Services
      </Button>
    </div>
  );
};

export default RegulatoryServicesEditor;
